/**
 * Utility functions for handling broken image URLs in case studies
 */

/** Placeholder shown when a case study image is missing or fails to load. */
export const DEFAULT_PROFILE_IMG = "/default-profile.png";

export interface BrokenImageInfo {
  caseStudyId: number;
  caseStudyTitle: string;
  field: "heroImage" | "companyLogo" | "venueImage";
  brokenUrl: string;
  index?: number; // for venue images array
}

/**
 * Check if a URL is a broken Google Images redirect
 */
export function isBrokenGoogleImageUrl(url: string): boolean {
  return url.includes("images.app.goo.gl");
}

/**
 * Check if a string is usable as an `<img>` src.
 *
 * Accepts absolute URLs (`https://…`) and site-relative paths (`/logo.png`).
 * `new URL()` throws on site-relative paths because it has no base, so those
 * are accepted explicitly — otherwise a perfectly valid `/logo.png` would be
 * treated as broken and replaced with the placeholder before it can load.
 *
 * @param url - The candidate image URL from case study data.
 * @returns `true` if the string can be rendered as an image source.
 */
export function isValidImageUrl(url: string): boolean {
  if (!url || url === "???" || url.trim() === "") return false;
  if (url.startsWith("/")) return true; // site-relative (/path) or protocol-relative (//host) — both valid <img> srcs
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Upgrade an image URL to a scheme the HTTPS site can load.
 *
 * Insecure `http://` and protocol-relative `//` URLs are blocked as mixed
 * content on an HTTPS page, so they are rewritten to `https://`. Site-relative
 * and already-secure URLs are returned unchanged. Scheme matching is
 * case-insensitive so `HTTP://…` is upgraded too.
 *
 * Distinct from `ensureHttps` in `@/lib/utils/url-utils`, which leaves
 * `http://` unchanged and mangles site-relative paths — do not swap one for
 * the other.
 *
 * @param url - The raw image URL from case study data.
 * @returns The URL with a secure scheme.
 */
export function normalizeImageSrc(url: string): string {
  const scheme = url.toLowerCase();
  if (scheme.startsWith("//")) return `https:${url}`;
  if (scheme.startsWith("http://")) return `https://${url.slice("http://".length)}`;
  return url;
}

/**
 * Resolve a raw case-study image URL into a safe `<img>` src.
 *
 * Validates the URL, upgrades it to https, and falls back to the placeholder
 * when it is missing or unusable. Pairs validation with normalization so
 * callers can't accidentally skip one and reintroduce a broken/mixed-content
 * image.
 *
 * @param url - The raw image URL from case study data.
 * @returns The resolved src and whether the placeholder fallback was used.
 */
export function resolveImageSrc(url: string): { src: string; isError: boolean } {
  if (!isValidImageUrl(url)) return { src: DEFAULT_PROFILE_IMG, isError: true };
  return { src: normalizeImageSrc(url), isError: false };
}

/**
 * Get a fallback image URL based on the field type
 */
export function getFallbackImageUrl(field: "heroImage" | "companyLogo" | "venueImage"): string {
  switch (field) {
    case "heroImage":
      return "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=400&fit=crop";
    case "companyLogo":
      return DEFAULT_PROFILE_IMG;
    case "venueImage":
      return "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=400&fit=crop";
    default:
      return DEFAULT_PROFILE_IMG;
  }
}

/**
 * Analyze case studies data and find broken image URLs
 */
export function findBrokenImageUrls(caseStudies: any[]): BrokenImageInfo[] {
  const brokenImages: BrokenImageInfo[] = [];

  caseStudies.forEach((study) => {
    // Check hero image
    if (
      study.heroImage?.url &&
      (isBrokenGoogleImageUrl(study.heroImage.url) || !isValidImageUrl(study.heroImage.url))
    ) {
      brokenImages.push({
        caseStudyId: study.id,
        caseStudyTitle: study.title,
        field: "heroImage",
        brokenUrl: study.heroImage.url,
      });
    }

    // Check company logo
    if (
      study.company?.logo?.url &&
      (isBrokenGoogleImageUrl(study.company.logo.url) || !isValidImageUrl(study.company.logo.url))
    ) {
      brokenImages.push({
        caseStudyId: study.id,
        caseStudyTitle: study.title,
        field: "companyLogo",
        brokenUrl: study.company.logo.url,
      });
    }

    // Check venue images
    if (study.venue?.images && Array.isArray(study.venue.images)) {
      study.venue.images.forEach((img: any, index: number) => {
        if (img.url && (isBrokenGoogleImageUrl(img.url) || !isValidImageUrl(img.url))) {
          brokenImages.push({
            caseStudyId: study.id,
            caseStudyTitle: study.title,
            field: "venueImage",
            brokenUrl: img.url,
            index,
          });
        }
      });
    }
  });

  return brokenImages;
}

/**
 * Log broken image information to console
 */
export function logBrokenImages(caseStudies: any[]): void {
  const brokenImages = findBrokenImageUrls(caseStudies);

  if (brokenImages.length === 0) {
    console.log("✅ No broken image URLs found in case studies");
    return;
  }

  console.warn(`⚠️  Found ${brokenImages.length} broken image URL(s):`);
  brokenImages.forEach((info) => {
    const isInvalid = !isValidImageUrl(info.brokenUrl);
    const isGoogleRedirect = isBrokenGoogleImageUrl(info.brokenUrl);
    let issueType = "";
    if (isInvalid) issueType = " (Invalid URL)";
    else if (isGoogleRedirect) issueType = " (Broken Google Images redirect)";

    console.warn(
      `   - Case Study ID ${info.caseStudyId} (${info.caseStudyTitle}): ${info.field}${info.index !== undefined ? `[${info.index}]` : ""} = ${info.brokenUrl}${issueType}`
    );
  });

  console.log("\n💡 To fix these, update the case study data in your database or admin panel.");
}
